import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Expense, ExpenseCategory, TravelSubcategory, Stats, CATEGORIES } from '../types';
import {
  getAllExpenses,
  addExpense as addExpenseToDb,
  deleteExpense as deleteExpenseFromDb,
  updateExpense as updateExpenseInDb,
  calculateStorageUsed,
} from '../services/storage';

interface ExpenseContextType {
  expenses: Expense[];
  stats: Stats | null;
  isLoading: boolean;
  addExpense: (data: {
    category: ExpenseCategory;
    subcategory?: TravelSubcategory;
    description: string;
    amount?: number;
    date: string;
    file: File | Blob;
    filename: string;
    freightBillNumber?: string;
  }) => Promise<Expense>;
  deleteExpense: (id: string) => Promise<void>;
  updateExpense: (expense: Expense) => Promise<void>;
  getExpensesByFilter: (filter: {
    category?: ExpenseCategory;
    month?: number;
    year?: number;
  }) => Expense[];
  refreshExpenses: () => Promise<void>;
  markBOLAsSent: (id: string) => Promise<void>;
}

const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

export function ExpenseProvider({ children }: { children: ReactNode }) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadExpenses = useCallback(async () => {
    try {
      const loaded = await getAllExpenses();
      setExpenses(loaded.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
      await calculateStats(loaded);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const calculateStats = async (expenseList: Expense[]) => {
    const storage = await calculateStorageUsed();
    
    const byCategory = CATEGORIES.reduce((acc, cat) => {
      acc[cat.value] = expenseList.filter(e => e.category === cat.value).length;
      return acc;
    }, {} as Record<ExpenseCategory, number>);

    const byMonth: Record<string, number> = {};
    expenseList.forEach(e => {
      const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });

    const totalAmount = expenseList.reduce((sum, e) => sum + (e.amount || 0), 0);

    setStats({
      totalExpenses: expenseList.length,
      totalAmount,
      byCategory,
      byMonth,
      storageUsed: storage.bytes,
      storageUsedFormatted: storage.formatted,
    });
  };

  const fileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const addExpense = async (data: {
    category: ExpenseCategory;
    subcategory?: TravelSubcategory;
    description: string;
    amount?: number;
    date: string;
    file: File | Blob;
    filename: string;
    freightBillNumber?: string;
  }): Promise<Expense> => {
    const fileData = await fileToBase64(data.file);
    const dateObj = new Date(data.date);

    const expense: Expense = {
      id: uuidv4(),
      category: data.category,
      subcategory: data.subcategory,
      description: data.description,
      amount: data.amount,
      date: data.date,
      month: dateObj.getMonth() + 1,
      year: dateObj.getFullYear(),
      filename: data.filename,
      fileData,
      fileType: data.file.type || 'application/octet-stream',
      fileSize: data.file.size,
      createdAt: new Date().toISOString(),
      freightBillNumber: data.freightBillNumber,
      isSentToBennett: false,
    };

    await addExpenseToDb(expense);
    await loadExpenses();
    return expense;
  };

  const deleteExpense = async (id: string) => {
    await deleteExpenseFromDb(id);
    await loadExpenses();
  };

  const updateExpense = async (expense: Expense) => {
    await updateExpenseInDb(expense);
    await loadExpenses();
  };

  const markBOLAsSent = async (id: string) => {
    const expense = expenses.find(e => e.id === id);
    if (expense) {
      await updateExpenseInDb({ ...expense, isSentToBennett: true });
      await loadExpenses();
    }
  };

  const getExpensesByFilter = (filter: {
    category?: ExpenseCategory;
    month?: number;
    year?: number;
  }): Expense[] => {
    return expenses.filter(e => {
      if (filter.category && e.category !== filter.category) return false;
      if (filter.month && e.month !== filter.month) return false;
      if (filter.year && e.year !== filter.year) return false;
      return true;
    });
  };

  return (
    <ExpenseContext.Provider
      value={{
        expenses,
        stats,
        isLoading,
        addExpense,
        deleteExpense,
        updateExpense,
        getExpensesByFilter,
        refreshExpenses: loadExpenses,
        markBOLAsSent,
      }}
    >
      {children}
    </ExpenseContext.Provider>
  );
}

export function useExpenses() {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpenses must be used within an ExpenseProvider');
  }
  return context;
}





